import { useEffect, useState } from "react"
import {
	IconDescendingSortingOutline18 as ArrowDownNarrowWide,
	IconAscendingSortingOutline18 as ArrowUpNarrowWide,
	IconCheckOutline18 as Check,
	IconFlameOutline18 as Flame,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton, Popover, Shortcut, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { InboxSortTypes } from "@/lib/states/global-state"
import { useShortcut } from "@/lib"
import { useSortState, useThreadState } from "@/lib/states/global-state"
import { SortIcon } from "@/screens/app/nav-icons"

const SORTING_OPTIONS = [
	{
		label: "Important first",
		value: "important",
		icon: Flame,
	},
	{
		label: "Oldest first",
		value: "oldest",
		icon: ArrowUpNarrowWide,
	},
	{
		label: "Newest first",
		value: "newest",
		icon: ArrowDownNarrowWide,
	},
]

const SORTING_OPTIONS_DONE = [
	{
		label: "Oldest first",
		value: "oldest",
		icon: ArrowUpNarrowWide,
	},
	{
		label: "Newest first",
		value: "newest",
		icon: ArrowDownNarrowWide,
	},
]

/** Done views have nothing to rank by importance, so they fall back to newest first. */
export const useInboxSort = () => {
	const { sortBy, setSortBy } = useSortState()
	const { inboxState, assignedSubTab } = useThreadState()
	const isDoneView =
		assignedSubTab === "done" ||
		inboxState === "done" ||
		inboxState === "my-done"

	useEffect(() => {
		if (isDoneView && sortBy === "important") {
			setSortBy("newest")
		}
	}, [isDoneView, sortBy, setSortBy])

	return {
		sortBy,
		setSortBy,
		options: isDoneView ? SORTING_OPTIONS_DONE : SORTING_OPTIONS,
	}
}

export const InboxSortItems = ({ onSelect }: { onSelect?: () => void }) => {
	const { sortBy, setSortBy, options } = useInboxSort()
	return (
		<CommandGroup heading="Sort">
			{options.map((option) => {
				const isSelected = sortBy === option.value
				return (
					<CommandItem
						key={option.value}
						Icon={option.icon}
						onSelect={() => {
							setSortBy(option.value as InboxSortTypes)
							onSelect?.()
						}}
					>
						<div className="flex w-full items-center gap-2">
							<span className="flex-1 truncate">{option.label}</span>
							<Check
								className={cn("h-4 w-4 shrink-0", {
									"opacity-100": isSelected,
									"opacity-0": !isSelected,
								})}
							/>
						</div>
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
}

export const InboxSort = () => {
	const [isOpen, setIsOpen] = useState(false)

	useShortcut(
		"v",
		() => {
			setIsOpen(true)
		},
		{ preventDefault: true },
	)

	return (
		<Popover
			className="w-48 p-0"
			align="end"
			open={isOpen}
			onOpenChange={setIsOpen}
			content={
				<Command>
					<CommandList>
						<InboxSortItems onSelect={() => setIsOpen(false)} />
					</CommandList>
				</Command>
			}
		>
			<div>
				<Tooltip
					label={
						<div className="flex items-center gap-1">
							Sort
							<Shortcut>V</Shortcut>
						</div>
					}
				>
					<IconButton variant="ghost" Icon={SortIcon} />
				</Tooltip>
			</div>
		</Popover>
	)
}
